import { UpdatePayload, useUpdateWorkflow } from './useUpdateWorkflow'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { sendToApi } from 'api/client'
import { useState } from 'react'
import { WorkflowUpdateJobRecord } from 'stores/Workflows/Types'
import { buildErrorMessage } from '../../../utils'

interface Props {
  folderId: string
}

interface Options {
  onError: (e: Error) => void
  onSuccess: (data: { workflow_update_job_record: WorkflowUpdateJobRecord }) => void
}

export const usePollUpdateWorkflow = ({ folderId }: Props) => {
  const { mutate, isLoading: isMutating } = useUpdateWorkflow({ folderId })
  const [isPolling, setIsPolling] = useState(false)

  const mutateWithPolling = (
    { property_id, property_values, config, reassignments }: UpdatePayload,
    { onError, onSuccess }: Options,
  ) =>
    mutate(
      {
        property_id,
        property_values,
        config,
        reassignments,
      },
      {
        onSuccess: (data) => {
          const {
            workflow_update_job_record: { id },
          } = data
          setIsPolling(true)
          const poll = async () => {
            const response = await sendToApi<{ workflow_update_job_record: WorkflowUpdateJobRecord }>(
              `/folders/${folderId}/workflow_update_job_records/${id}`,
            )

            if (response.type !== SUCCESS_RESPONSE_TYPE) {
              setIsPolling(false)
              onError(
                new Error(
                  buildErrorMessage(response.data.errors) || `Failed to fetch workflow update job record ${id}`,
                ),
              )
              return
            }

            switch (response.data.workflow_update_job_record.status) {
              case 'successful':
                setIsPolling(false)
                onSuccess(data)
                break
              case 'processing':
              case 'pending':
                setTimeout(poll, 200)
                break
              default:
                setIsPolling(false)
                onError(new Error(`Workflow update job record ${id} failed`))
            }
          }
          poll()
        },
        onError: (e) => {
          setIsPolling(false)
          onError(e)
        },
      },
    )

  return { mutateWithPolling, isLoading: isMutating || isPolling }
}
